import { useState } from 'react';
import { Wifi, WifiOff, DollarSign, MessageSquare, CheckCircle2, Clock, Users } from 'lucide-react';
import type { UseCollaborativeGameResult } from '../../../hooks/useCollaborativeGame';
import type { GameEvent, LiveFeatureState, LiveParticipant } from '../../../types/collaborativeGame';

interface LivePurchasingViewProps {
  game: UseCollaborativeGameResult;
  myName: string;
  isHost: boolean;
  onEndSession: () => void;
}

interface PurchaseModalState {
  feature: LiveFeatureState;
  note: string;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function EventFeedItem({ event }: { event: GameEvent }) {
  const data = event.eventData as Record<string, unknown>;

  const label = () => {
    switch (event.eventType) {
      case 'join':
        return <span><strong>{event.participantName}</strong> joined{(data.isHost as boolean) ? ' (host)' : ''}</span>;
      case 'session_started':
        return <span>Session started by <strong>{event.participantName}</strong></span>;
      case 'purchase':
        return (
          <span>
            <strong>{event.participantName}</strong> bought <strong>{data.featureName as string}</strong> for ${data.price as number}
            {data.note ? <span className="block text-[var(--text-ter)] text-xs mt-0.5 italic">"{data.note as string}"</span> : null}
          </span>
        );
      case 'remove_purchase':
        return <span><strong>{event.participantName}</strong> removed <strong>{data.featureName as string}</strong></span>;
      case 'comment':
        return <span><strong>{event.participantName}</strong>: <em>{data.text as string}</em></span>;
      case 'done':
        return <span><strong>{event.participantName}</strong> finished spending</span>;
      case 'session_ended':
        return <span>Session ended</span>;
      default:
        return <span>{event.eventType}</span>;
    }
  };

  const color = {
    join: 'text-[var(--text-ter)]',
    session_started: 'text-[var(--green)] dark:text-[var(--text-ter)]',
    purchase: 'text-[var(--green)] dark:text-[var(--text-ter)]',
    remove_purchase: 'text-[var(--mustard)]',
    comment: 'text-[var(--text-ter)]',
    done: 'text-[var(--green)] dark:text-[var(--text-ter)]',
    session_ended: 'text-[var(--text-ter)]',
  }[event.eventType] ?? 'text-[var(--text-sec)]';

  return (
    <div className="flex gap-2 text-sm py-1 border-b border-[var(--rule)] last:border-0">
      <span className="text-[var(--text-ter)] text-xs shrink-0 pt-0.5">{formatTime(event.createdAt)}</span>
      <span className={color}>{label()}</span>
    </div>
  );
}

function ParticipantChip({ participant }: { participant: LiveParticipant }) {
  return (
    <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium border ${
      participant.isDone
        ? 'bg-[var(--green-dim)] border-[var(--green)] text-[var(--green)] dark:text-[var(--text-ter)]'
        : 'bg-[var(--bg-raised)] border-[var(--rule)] text-[var(--text-sec)]'
    }`}>
      {participant.isDone
        ? <CheckCircle2 className="h-3 w-3" />
        : <Clock className="h-3 w-3 text-[var(--text-ter)]" />}
      {participant.name}
      <span className="text-[var(--text-ter)]">${participant.remainingBudget.toFixed(0)} left</span>
    </div>
  );
}

export function LivePurchasingView({ game, myName, isHost, onEndSession }: LivePurchasingViewProps) {
  const [modal, setModal] = useState<PurchaseModalState | null>(null);
  const [commentText, setCommentText] = useState('');
  const [showCommentBox, setShowCommentBox] = useState(false);

  const me = game.participants.find(p => p.name === myName);
  const myBudget = me?.remainingBudget ?? 0;
  const myPurchases = me?.purchases ?? [];

  const getPurchaseQty = (featureId: string) =>
    myPurchases.find(p => p.featureId === featureId)?.quantity ?? 0;

  const canAfford = (price: number) => myBudget >= price;
  const myDone = me?.isDone ?? false;

  const donePct = game.participants.length > 0
    ? Math.round((game.participants.filter(p => p.isDone).length / game.participants.length) * 100)
    : 0;

  const handleBuyClick = (feature: LiveFeatureState) => {
    if (!canAfford(feature.price) || myDone) return;
    setModal({ feature, note: '' });
  };

  const handleConfirmPurchase = async () => {
    if (!modal) return;
    await game.purchase(modal.feature.id, modal.note.trim() || undefined);
    setModal(null);
  };

  const handleRemove = async (featureId: string) => {
    if (myDone) return;
    await game.removePurchase(featureId);
  };

  const handleComment = async () => {
    if (!commentText.trim()) return;
    await game.addComment(commentText.trim());
    setCommentText('');
    setShowCommentBox(false);
  };

  const handleMarkDone = async () => {
    await game.markDone();
  };

  return (
    <div className="flex h-full gap-0">
      {/* Main buying area */}
      <div className="flex-1 overflow-y-auto p-4 min-w-0">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              {game.isConnected
                ? <Wifi className="h-4 w-4 text-[var(--green)]" />
                : <WifiOff className="h-4 w-4 text-[var(--coral)]" />}
              <span className="text-sm text-[var(--text-ter)]">{game.isConnected ? 'Live' : 'Reconnecting…'}</span>
            </div>
            <h2 className="text-xl font-bold text-[var(--text-pri)]">
              {myDone ? 'Waiting for others…' : `${myName}'s Budget: $${myBudget.toFixed(0)}`}
            </h2>
          </div>
          <div className="flex flex-col items-end gap-2 shrink-0">
            {!myDone && (
              <button
                onClick={handleMarkDone}
                className="px-3 py-1.5 text-sm bg-[var(--green)] text-white hover:bg-[var(--green)] transition-colors flex items-center gap-1"
              >
                <CheckCircle2 className="h-4 w-4" />
                Done spending
              </button>
            )}
            {isHost && (
              <button
                onClick={onEndSession}
                className="px-3 py-1.5 text-sm bg-[var(--slate)] text-white hover:bg-[var(--bg-raised)] transition-colors"
              >
                End session
              </button>
            )}
          </div>
        </div>

        {/* Participants */}
        <div className="flex flex-wrap gap-2 mb-4">
          {game.participants.map(p => (
            <ParticipantChip key={p.name} participant={p} />
          ))}
          {game.participants.length === 0 && (
            <span className="text-sm text-[var(--text-ter)] flex items-center gap-1">
              <Users className="h-4 w-4" /> Waiting for participants…
            </span>
          )}
        </div>

        {/* Progress bar */}
        {game.participants.length > 0 && (
          <div className="mb-5">
            <div className="flex justify-between text-xs text-[var(--text-ter)] mb-1">
              <span>{game.participants.filter(p => p.isDone).length} of {game.participants.length} finished</span>
              <span>{donePct}%</span>
            </div>
            <div className="w-full bg-[var(--hover-tint)] rounded-full h-1.5">
              <div className="bg-[var(--green)] h-1.5 rounded-full transition-all" style={{ width: `${donePct}%` }} />
            </div>
          </div>
        )}

        {/* Feature cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
          {game.features.map(feature => {
            const qty = getPurchaseQty(feature.id);
            const affordable = canAfford(feature.price);
            const hasBought = qty > 0;

            return (
              <div
                key={feature.id}
                className={`relative  border-2 p-4 transition-all ${
                  myDone
                    ? 'border-[var(--rule)] bg-[var(--surface)] opacity-70'
                    : hasBought
                    ? 'border-[var(--green)] bg-[var(--green-dim)] shadow-sm'
                    : affordable
                    ? 'border-[var(--rule)] bg-[var(--bg-raised)] hover:border-[var(--slate)] hover:shadow-sm cursor-pointer'
                    : 'border-[var(--rule)] bg-[var(--surface)] opacity-50'
                }`}
                onClick={() => !hasBought && handleBuyClick(feature)}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h3 className="font-semibold text-[var(--text-pri)] text-sm leading-tight">{feature.name}</h3>
                  <div className="flex items-center gap-1 shrink-0 text-[var(--text-sec)] text-sm font-medium">
                    <DollarSign className="h-3.5 w-3.5" />
                    {feature.price}
                  </div>
                </div>

                {/* Total purchase indicator */}
                {feature.totalPurchases > 0 && (
                  <div className="text-xs text-[var(--text-ter)] mb-2">
                    {feature.totalPurchases} bought by {feature.buyerNames.join(', ')}
                  </div>
                )}

                {hasBought ? (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[var(--green)] dark:text-[var(--text-ter)] font-medium">You bought {qty}×</span>
                    <button
                      onClick={e => { e.stopPropagation(); handleRemove(feature.id); }}
                      className="text-xs text-[var(--coral)] hover:text-[var(--coral)] underline"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div className="text-xs text-[var(--text-ter)]">
                    {affordable ? 'Click to buy' : `Need $${(feature.price - myBudget).toFixed(0)} more`}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Comment box */}
        <div className="mt-4">
          {showCommentBox ? (
            <div className="flex gap-2">
              <input
                type="text"
                value={commentText}
                onChange={e => setCommentText(e.target.value)}
                placeholder="Say something to the group…"
                className="flex-1 px-3 py-2 text-sm border border-[var(--rule)] focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                onKeyDown={e => e.key === 'Enter' && handleComment()}
                autoFocus
              />
              <button onClick={handleComment} disabled={!commentText.trim()} className="px-3 py-2 bg-[var(--slate)] text-white text-sm hover:bg-[var(--slate)] disabled:opacity-50">
                Send
              </button>
              <button onClick={() => setShowCommentBox(false)} className="px-3 py-2 text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)]">
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowCommentBox(true)}
              className="flex items-center gap-1.5 text-sm text-[var(--text-ter)] hover:text-[var(--text-ter)]"
            >
              <MessageSquare className="h-4 w-4" />
              Add comment to transcript
            </button>
          )}
        </div>
      </div>

      {/* Event feed sidebar */}
      <div className="w-64 shrink-0 border-l border-[var(--rule)] bg-[var(--surface)] flex flex-col">
        <div className="px-3 py-2 border-b border-[var(--rule)]">
          <h3 className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide">Live transcript</h3>
        </div>
        <div className="flex-1 overflow-y-auto px-3 py-2 space-y-0">
          {game.events.length === 0 ? (
            <p className="text-xs text-[var(--text-ter)] pt-2">Nothing yet — waiting for activity…</p>
          ) : (
            [...game.events].reverse().map(event => (
              <EventFeedItem key={event.id} event={event} />
            ))
          )}
        </div>
      </div>

      {/* Purchase confirmation modal */}
      {modal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--bg-raised)] shadow-xl max-w-sm w-full p-6">
            <h3 className="text-lg font-bold text-[var(--text-pri)] mb-1">Buy {modal.feature.name}?</h3>
            <p className="text-sm text-[var(--text-sec)] mb-4">
              ${modal.feature.price} from your budget. You'll have ${(myBudget - modal.feature.price).toFixed(0)} left.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                Why? <span className="text-[var(--text-ter)] font-normal">(optional — goes in the transcript)</span>
              </label>
              <input
                type="text"
                value={modal.note}
                onChange={e => setModal(m => m ? { ...m, note: e.target.value } : null)}
                placeholder="e.g. This unblocks our Q3 launch"
                className="w-full px-3 py-2 border border-[var(--rule)] text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                onKeyDown={e => e.key === 'Enter' && handleConfirmPurchase()}
                autoFocus
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleConfirmPurchase}
                className="flex-1 px-4 py-2.5 bg-[var(--slate)] text-white hover:bg-[var(--slate)] font-medium transition-colors"
              >
                Buy
              </button>
              <button
                onClick={() => setModal(null)}
                className="flex-1 px-4 py-2.5 bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)] font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
